import { useState, useCallback } from "react";
import type {
  FlowVersionSummaryViewModel,
  FlowConfigViewModel,
  CreateFlowConfigRequest,
  ActivateFlowRequest,
} from "@models/doctype";
import { docTypeApi } from "@src/services/doctypeApi";

export interface IFlowConfigState {
  flowVersions: FlowVersionSummaryViewModel[];
  selectedFlowConfig: FlowConfigViewModel | null;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  fetchFlowVersions: (docTypeId: string) => Promise<void>;
  fetchFlowDetail: (
    docTypeId: string,
    flowBaseId: string,
    version: number,
  ) => Promise<FlowConfigViewModel | null>;
  createFlowConfig: (
    docTypeId: string,
    data: CreateFlowConfigRequest,
  ) => Promise<FlowConfigViewModel | null>;
  activateFlow: (
    docTypeId: string,
    data: ActivateFlowRequest,
  ) => Promise<FlowConfigViewModel | null>;
}

export const useFlowConfig = (): IFlowConfigState => {
  const [flowVersions, setFlowVersions] = useState<
    FlowVersionSummaryViewModel[]
  >([]);
  const [selectedFlowConfig, setSelectedFlowConfig] =
    useState<FlowConfigViewModel | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const fetchFlowVersions = useCallback(async (docTypeId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const versions = await docTypeApi.getFlowVersions(docTypeId);
      setFlowVersions(versions);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch flow versions";
      setError(errorMessage);
      console.error("Error fetching flow versions:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchFlowDetail = useCallback(
    async (
      docTypeId: string,
      flowBaseId: string,
      version: number,
    ): Promise<FlowConfigViewModel | null> => {
      try {
        setIsLoading(true);
        setError(null);
        const config = await docTypeApi.getFlowDetailByVersion(
          docTypeId,
          flowBaseId,
          version,
        );
        setSelectedFlowConfig(config);
        return config;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch flow detail";
        setError(errorMessage);
        console.error("Error fetching flow detail:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const createFlowConfig = useCallback(
    async (
      docTypeId: string,
      data: CreateFlowConfigRequest,
    ): Promise<FlowConfigViewModel | null> => {
      try {
        setIsLoading(true);
        setError(null);
        const newConfig = await docTypeApi.createFlowConfig(docTypeId, data);
        await fetchFlowVersions(docTypeId);
        return newConfig;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create flow config";
        setError(errorMessage);
        console.error("Error creating flow config:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFlowVersions],
  );

  const activateFlow = useCallback(
    async (
      docTypeId: string,
      data: ActivateFlowRequest,
    ): Promise<FlowConfigViewModel | null> => {
      try {
        setIsLoading(true);
        setError(null);
        const activatedConfig = await docTypeApi.activateFlow(docTypeId, data);
        await fetchFlowVersions(docTypeId);
        return activatedConfig;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to activate flow";
        setError(errorMessage);
        console.error("Error activating flow:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFlowVersions],
  );

  return {
    flowVersions,
    selectedFlowConfig,
    isLoading,
    error,
    clearError,
    fetchFlowVersions,
    fetchFlowDetail,
    createFlowConfig,
    activateFlow,
  };
};
